import React from 'react';

import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';
import { Link } from '~/lib/react-router-dom';

import { Alert, Box, Chip, Container, Stack, Tab, Tabs } from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';

import { getBusiness } from '~/services/BusinessTraveller/BusinessTravellerService';

import BusinessDetailsHeaderActions from './BusinessDetailsHeaderActions';
import ActivityTab from './Tabs/ActivityTab';
import BusinessSettingTab from './Tabs/BusinessSettingTab';
import BusinessTab from './Tabs/BusinessTab';
import EmployeesTab from './Tabs/EmployeesTab';
import FrequentFlyerTab from './Tabs/FrequentFlyerTab';
import NdcFlightsTab from './Tabs/NdcFlightsTab';
import OrderTab from './Tabs/OrdersTab';
import ServiceFeeTab from './Tabs/ServiceFeeTab';

const tabsMap = {
  business: {
    component: BusinessTab,
    label: 'Business',
  },
  serviceFee: {
    component: ServiceFeeTab,
    label: 'Service Fees',
  },
  employees: {
    component: EmployeesTab,
    label: 'Employees',
  },
  frequentFlyer: {
    component: FrequentFlyerTab,
    label: 'Frequent Flyer',
  },
  orders: {
    component: OrderTab,
    label: 'Orders',
  },
  activity: {
    component: ActivityTab,
    label: 'Activity Log',
  },
  ndcFlights: {
    component: NdcFlightsTab,
    label: 'NDC Flights',
  },
  businessSetting: {
    component: BusinessSettingTab,
    label: 'Business Setting',
  },
};

const tabsList = Object.keys(tabsMap);

type TabKey = keyof typeof tabsMap;

export default function BusinessTabPage() {
  const { businessId, tab } = useParams<'businessId' | 'tab'>();
  const key = tab || 'business';

  const currentTab = tabsMap[key];

  const { data: business } = useQuery({
    queryKey: ['business', businessId],
    queryFn: () => getBusiness(businessId),
    enabled: !!businessId,
  });

  const isDeactivated = !!business?.deletedAt;
  const title = `Business / ${currentTab.label}`;

  return (
    <Container maxWidth="xl">
      <Helmet>
        <title>{title}</title>
      </Helmet>

      <PageHeader
        title={
          <Stack direction="row" alignItems="center" spacing={1}>
            <span>{title}</span>
            {isDeactivated && <Chip label="Deactivated" size="small" color="error" variant="outlined" />}
          </Stack>
        }
      >
        <BusinessDetailsHeaderActions businessId={businessId} />
      </PageHeader>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={key}>
          {tabsList.map((tab) => (
            <Tab
              component={Link}
              key={tab}
              label={tabsMap[tab].label}
              to={`/lebusiness/${businessId}/${tab}`}
              value={tab}
            />
          ))}
        </Tabs>
      </Box>

      <Box mt={4}>
        {isDeactivated && (
          <Alert severity="error" sx={{ mb: 2, width: 'fit-content' }}>
            This business has been deactivated. Records have been deactivated and no accounts can sign in.
          </Alert>
        )}
        <Box component={currentTab.component} />
      </Box>
    </Container>
  );
}
